import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { BoldMedBodyText } from '../styles/Text'

import { sortByFileSize, sortByDateKey } from '../../util/sorting'
import { TableHeaderButton } from '../common/Button'
import DatabaseFile from './DatabaseFile'
import PIRFile from './PIRFile'

const HeadingsContainer = styled.div`
  display: flex;
  gap: 15px;
`

const Headings = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px 15px;
`

const FileName = styled.div`
  width: 60%;

  @media only screen and (max-width: 800px) {
    width: 100%;
  }
`

const SizeDateHeading = styled.div`
  width: 20%;

  @media only screen and (max-width: 800px) {
    display: none;
  }
`
const SizeDateHeadingRight = styled(SizeDateHeading)`
  text-align: right;
`

const UnfoldMoreIconRight = styled(UnfoldMoreIcon)`
  margin-right: -10px;
`

const ButtonColumn = styled.div`
  width: 50px;
`

const NoFilesText = styled(BoldMedBodyText)`
  text-align: center;
  padding: 15px 0 15px 15px;
  line-height: 20px;
`

function sortByKey(key) {
  return function (a, b) {
    const aValue = (key === 'owner' ? a.createdBy.name : a[key]).toUpperCase()
    const bValue = (key === 'owner' ? b.createdBy.name : b[key]).toUpperCase()

    if (aValue < bValue) return -1
    if (aValue > bValue) return 1
    return 0
  }
}

function FileList({ type = 'pir', files, requestId, onDelete = () => {}, buttonAction = 'delete' }) {
  const [sortedFiles, setSortedFiles] = useState(files)
  const [sortBy, setSortBy] = useState()
  const [sortAscending, setSortAscending] = useState(true)

  function sort(newSortBy) {
    if (newSortBy === sortBy) {
      setSortedFiles([...sortedFiles.reverse()])
      setSortAscending(!sortAscending)
    } else {
      setSortBy(newSortBy)
      setSortAscending(true)

      if (newSortBy === 'date') setSortedFiles(sortedFiles.sort(sortByDateKey('lastModified')))
      else if (newSortBy === 'size') setSortedFiles(sortedFiles.sort(sortByFileSize))
      else setSortedFiles(sortedFiles.sort(sortByKey(newSortBy)))
    }
  }

  useEffect(() => {
    let sortedFileList = files
    if (sortBy) {
      if (sortBy === 'date') {
        sortedFileList = files.sort(sortByDateKey('lastModified'))
      } else if (sortBy === 'size') {
        sortedFileList = files.sort(sortByFileSize)
      } else {
        sortedFileList = files.sort(sortByKey(sortBy))
      }
      if (!sortAscending) {
        sortedFileList = [...sortedFileList.reverse()]
      }
    }
    setSortedFiles(sortedFileList)
  }, [files])

  return (
    <div>
      <HeadingsContainer>
        <Headings>
          <FileName>
            <TableHeaderButton
              variant="text"
              onClick={() => sort(type === 'pir' ? 'name' : 'filename')}
              active={((type === 'pir' && sortBy === 'name') || (type === 'database' && sortBy === 'filename')).toString()}>
              Name <UnfoldMoreIcon />
            </TableHeaderButton>
          </FileName>
          <SizeDateHeading>
            <TableHeaderButton variant="text" onClick={() => sort('size')} active={(sortBy === 'size').toString()}>
              File Size <UnfoldMoreIcon />
            </TableHeaderButton>
          </SizeDateHeading>
          <SizeDateHeadingRight>
            <TableHeaderButton variant="text" onClick={() => sort('date')} active={(sortBy === 'date').toString()}>
              Last Modified <UnfoldMoreIconRight />
            </TableHeaderButton>
          </SizeDateHeadingRight>
        </Headings>
        <ButtonColumn />
      </HeadingsContainer>

      {sortedFiles.length > 0 ? (
        sortedFiles.map((file, i) => (
          <div key={i} data-testid="table-row">
            {type === 'pir' ? (
              <PIRFile file={file} pirId={requestId} onDelete={onDelete} buttonAction={buttonAction} />
            ) : (
              <DatabaseFile
                file={file}
                onDelete={onDelete}
                buttonAction={file.associationType && file.associationType === 'CATEGORY' ? 'download' : buttonAction}
              />
            )}
          </div>
        ))
      ) : (
        <NoFilesText>There are currently no files attached to this request</NoFilesText>
      )}
    </div>
  )
}

FileList.propTypes = {
  type: PropTypes.oneOf(['pir', 'database']),
  files: PropTypes.array.isRequired,
  requestId: (props, propName) => {
    if (props['type'] === 'pir' && (props[propName] === undefined || typeof props[propName] !== 'string')) {
      return new Error('A requestId is required when type is pir')
    }
  },
  onDelete: PropTypes.func,
  buttonAction: PropTypes.oneOf(['delete', 'download'])
}

export default FileList
